/* Magnetix shared upload + print helpers (theme asset: mt-helpers.js) — US store (English messages)
 * Port of the MagnetixTiles helper. Same Cloudinary account and preset as MagnetixTiles.
 *
 * MTH.prepareFile(file)  -> Promise<File>  Files over Cloudinary's 10 MB limit are re-encoded in the
 *                                          browser, keeping as many pixels as possible. Files under it are untouched.
 * MTH.upload(file, onProgress) -> Promise<{url, width, height}>  rejects with a customer-readable message
 * MTH.displayUrl(url, px) -> light JPG copy for the screen (originals can be 10+ MB, HEIC or TIFF)
 * MTH.printUrl(o)         -> full-resolution exact crop of the original, for production
 */
(function () {
  'use strict';
  var CLOUD = 'du3eofguy', PRESET = 'magnetixtiles_uploads';
  var MAX_BYTES = 9.5 * 1024 * 1024; // Cloudinary account limit is 10 MB per image

  function decode(file) {
    return new Promise(function (res, rej) {
      var u = URL.createObjectURL(file), im = new Image();
      im.onload = function () { res(im); };
      im.onerror = function () { URL.revokeObjectURL(u); rej(); };
      im.src = u;
    });
  }
  function encode(im, k, q) {
    return new Promise(function (res) {
      try {
        var c = document.createElement('canvas');
        c.width = Math.round(im.naturalWidth * k); c.height = Math.round(im.naturalHeight * k);
        var x = c.getContext('2d');
        x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height); // transparent PNGs: white, not black
        x.drawImage(im, 0, 0, c.width, c.height);
        c.toBlob(function (b) { res(b); }, 'image/jpeg', q);
      } catch (e) { res(null); }
    });
  }

  // Tries full resolution first, then 24 MP, then 16 MP (iPhone Safari's canvas ceiling).
  function prepareFile(file) {
    if (!file || file.size <= MAX_BYTES) return Promise.resolve(file);
    return decode(file).then(function (im) {
      var px = im.naturalWidth * im.naturalHeight, steps = [];
      [px, 24e6, 16e6].forEach(function (p) {
        var k = Math.min(1, Math.sqrt(p / px));
        [0.92, 0.85, 0.78].forEach(function (q) { steps.push([k, q]); });
      });
      var name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
      function next(i) {
        if (i >= steps.length) return file;
        return encode(im, steps[i][0], steps[i][1]).then(function (b) {
          if (b && b.size > 0 && b.size <= MAX_BYTES) return new File([b], name, { type: 'image/jpeg' });
          return next(i + 1);
        });
      }
      return next(0);
    }, function () { return file; });
  }

  function upload(file, onProgress) {
    return prepareFile(file).then(function (f) {
      return new Promise(function (resolve, reject) {
        var fd = new FormData();
        fd.append('file', f);
        fd.append('upload_preset', PRESET);
        var xhr = new XMLHttpRequest();
        xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + CLOUD + '/image/upload');
        if (onProgress) xhr.upload.onprogress = function (e) { if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100)); };
        xhr.onload = function () {
          var r = null;
          try { r = JSON.parse(xhr.responseText); } catch (e) {}
          if (r && r.secure_url) { resolve({ url: r.secure_url, width: r.width, height: r.height }); return; }
          var m = r && r.error && r.error.message ? r.error.message : 'HTTP ' + xhr.status;
          reject(/size too large/i.test(m)
            ? 'This photo is over 10 MB and your browser could not shrink it. Please choose a JPG or a smaller photo.'
            : 'We could not upload this photo (' + m + '). Please try again.');
        };
        xhr.onerror = function () { reject('We could not upload this photo. Please check your internet connection and try again.'); };
        xhr.ontimeout = xhr.onerror;
        xhr.timeout = 180000;
        xhr.send(fd);
      });
    });
  }

  function displayUrl(url, px) {
    if (!url || url.indexOf('/upload/') < 0) return url;
    px = px || 2400;
    return url.replace('/upload/', '/upload/f_jpg,q_90,w_' + px + ',h_' + px + ',c_limit/');
  }

  function f4(n) { return (Math.round(n * 10000) / 10000).toFixed(4); }
  function publicId(url) {
    var m = url.match(/\/upload\/(?:v\d+\/)?(.+?)(\.[a-z0-9]+)?$/i);
    return m ? m[1].replace(/\//g, ':') : '';
  }
  // Cloudinary limits on generated images: 25 megapixels and 10 MB. Print files stay just under 24 MP
  // and step down JPEG quality only when the 10 MB limit requires it.
  var MAX_OUT_PX = 24e6;
  // o: { url, imgRatio (true w/h as displayed), frameRatio (print w/h), cropXf, cropYf, cropScale, bw, natW, natH }
  // q: JPEG quality. cap: last-resort size limit for photos whose pixel size is unknown.
  function printUrl(o, q, cap) {
    if (!o || !o.url || !o.imgRatio || !o.frameRatio) return '';
    var r = o.imgRatio, cr = o.frameRatio, s = o.cropScale || 1;
    var fw = r >= cr ? r / cr : 1, fh = r >= cr ? 1 : cr / r;
    var fx = 1 / (s * fw), fy = 1 / (s * fh);
    var u0 = 0.5 - (0.5 + (o.cropXf || 0)) / (s * fw), v0 = 0.5 - (0.5 + (o.cropYf || 0)) / (s * fh);
    var tail = (cap ? 'c_limit,w_5000,h_5000/' : '') + (o.bw ? 'e_grayscale/' : '') + 'q_' + (q || 100) + '/';
    var W = o.natW, H = o.natH;
    if (W && H && (W >= H) !== (r >= 1)) { var t = W; W = H; H = t; } // match the displayed (EXIF-rotated) orientation
    var e = 0.0005;
    if (u0 >= -e && v0 >= -e && u0 + fx <= 1 + e && v0 + fy <= 1 + e) {
      // Crop is fully inside the photo: exact relative crop of the original
      u0 = Math.max(0, u0); v0 = Math.max(0, v0);
      fx = Math.min(1 - u0, fx); fy = Math.min(1 - v0, fy);
      var c = 'c_crop,x_' + f4(u0) + ',y_' + f4(v0) + ',w_' + f4(fx) + ',h_' + f4(fy) + '/';
      if (W && H) {
        var px = fx * W * fy * H;
        if (px > MAX_OUT_PX) c += 'c_scale,w_' + Math.floor(fx * W * Math.sqrt(MAX_OUT_PX / px)) + '/';
      }
      return o.url.replace('/upload/', '/upload/' + c + tail);
    }
    // Customer zoomed out: original placed on the print canvas,
    // blank areas get a soft blurred fill (to be completed with AI before printing)
    var pid = publicId(o.url);
    if (!W || !H || !pid) return '';
    var ix0 = Math.max(0, u0), iy0 = Math.max(0, v0), ix1 = Math.min(1, u0 + fx), iy1 = Math.min(1, v0 + fy);
    if (ix1 <= ix0 || iy1 <= iy0) return '';
    var k = Math.min(1, Math.sqrt(MAX_OUT_PX / (fx * W * fy * H)));
    var CW = Math.round(fx * W * k), CH = Math.round(fy * H * k);
    var X = Math.round((ix0 - u0) * W * k), Y = Math.round((iy0 - v0) * H * k);
    var OW = Math.max(1, Math.round((ix1 - ix0) * W * k));
    return o.url.replace('/upload/', '/upload/c_fill,w_' + CW + ',h_' + CH + ',e_blur:2000/l_' + pid +
      '/c_crop,x_' + f4(ix0) + ',y_' + f4(iy0) + ',w_' + f4(ix1 - ix0) + ',h_' + f4(iy1 - iy0) + '/c_scale,w_' + OW +
      '/fl_layer_apply,g_north_west,x_' + X + ',y_' + Y + '/' + tail);
  }

  // Asks for the best file Cloudinary will generate. A HEAD request generates it and reports success
  // without downloading it on the customer's phone. Returns '' if nothing works (the order still has
  // the original photo and the preview), so a broken link is never saved on an order.
  function printFile(o) {
    var tries = [[100], [95], [92], [88], [90, true], [80, true]];
    function attempt(i) {
      if (i >= tries.length) return Promise.resolve('');
      var u = printUrl(o, tries[i][0], tries[i][1]);
      if (!u) return Promise.resolve('');
      return fetch(u, { method: 'HEAD' }).then(function (r) {
        return r.ok ? u : attempt(i + 1);
      }, function () { return u; }); // network hiccup on the customer's side: keep the link rather than block the order
    }
    return attempt(0);
  }
  window.MTH = { prepareFile: prepareFile, upload: upload, displayUrl: displayUrl, printUrl: printUrl, printFile: printFile, MAX_BYTES: MAX_BYTES };
})();
